// api-lambda's reservations writer (reservations.service.ts:450) normalizes
// any incoming Hospitable status through getStatusEnumValue, which only keeps
// the values in the backend RESERVATION_STATUSES enum:
//   accepted | cancelled | canceled | pending | staying
// Anything else becomes undefined. The reports endpoint then narrows further
// to {accepted, cancelled, canceled, staying}. So the lists below cover what
// can realistically land in _item.status on the frontend.
//
// EXPIRED is exposed as a UI filter option but the writer excludes 'expired'
// at ingest (see compareReservations.excludedStatuses in api-lambda), so it
// typically matches nothing — EXPIRED_STATUSES is kept as a defensive case
// for legacy data that predates the exclusion.

import { RESERVATION_STATUSES } from "interfaces/reservations";

export const ACCEPTED_STATUSES = ["accepted", "staying"];

export const CANCELLED_STATUSES = ["cancelled", "canceled"];

export const PENDING_STATUSES = ["pending"];

export const EXPIRED_STATUSES = ["expired"];

const lower = (s: string | undefined): string => (s ?? "").toLowerCase();

export const isAccepted = (status: string | undefined): boolean =>
	ACCEPTED_STATUSES.includes(lower(status));

export const isCancelled = (status: string | undefined): boolean =>
	CANCELLED_STATUSES.includes(lower(status));

export const isPending = (status: string | undefined): boolean =>
	PENDING_STATUSES.includes(lower(status));

export const isExpired = (status: string | undefined): boolean =>
	EXPIRED_STATUSES.includes(lower(status));

// Test an item's raw status against a canonical filter value. The signature
// accepts any string (or null/undefined) so callers don't need to cast
// possibly-stale Redux values into the enum — unknown / falsy filter values
// are treated as "no filter" and pass everything through, so we never
// blank-screen on bogus persisted state.
export const matchesStatusFilter = (
	itemStatus: string | undefined,
	filterValue: string | null | undefined,
): boolean => {
	if (!filterValue) return true;
	switch (filterValue) {
		case RESERVATION_STATUSES.ACCEPTED:
			return isAccepted(itemStatus);
		case RESERVATION_STATUSES.CANCELLED:
			return isCancelled(itemStatus);
		case RESERVATION_STATUSES.PENDING:
			return isPending(itemStatus);
		case RESERVATION_STATUSES.EXPIRED:
			return isExpired(itemStatus);
		default:
			return true;
	}
};